/**
 * Admin Sourcing — talent-acquisition pipeline (iter 143).
 *
 * Admin-only. Filter-search Apollo → dedupe → reveal emails →
 * queue invites → review preview → send.
 *
 * Every invite carries a CAN-SPAM footer + one-click unsubscribe.
 * Rate limited server-side to 50/day + 60-120s randomized pacing.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
    Loader2, Search, UserPlus, Eye, Mail, Send, Filter, Shield,
    RefreshCw, X, AlertTriangle, CheckCircle2, Ban, ExternalLink,
    Pencil, Save,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { toast } from "@/components/ui/sonner";
import api, { formatApiError } from "@/lib/api";

const TAB_KEYS = ["search", "queue", "log", "suppression", "settings"];
const TAB_LABELS = {
    search: "Search",
    queue: "Invite queue",
    log: "Contact log",
    suppression: "Suppression",
    settings: "Template & settings",
};

// ============================================================
// Search + results tab
// ============================================================
const SearchTab = () => {
    const [filters, setFilters] = useState({
        titles: "", location: "", skills: "", seniority: "", per_page: 25, page: 1,
    });
    const [busy, setBusy] = useState(false);
    const [results, setResults] = useState([]);
    const [duplicates, setDuplicates] = useState(0);
    const [pagination, setPagination] = useState(null);
    const [selected, setSelected] = useState({}); // key = row.apollo_person_id or email
    const [credits, setCredits] = useState(null);
    // iter 147 — template picker so admins can swap templates per campaign.
    const [templateLibrary, setTemplateLibrary] = useState([]);
    const [templateVersion, setTemplateVersion] = useState(""); // "" = use active
    const [revealing, setRevealing] = useState({});
    const [queuing, setQueuing] = useState(false);
    const [previewOpen, setPreviewOpen] = useState(false);
    const [preview, setPreview] = useState(null);

    const loadCredits = useCallback(async () => {
        try {
            const { data } = await api.get("/admin/sourcing/credits");
            setCredits(data);
        } catch { /* non-blocking */ }
    }, []);

    const loadTemplates = useCallback(async () => {
        try {
            const { data } = await api.get("/admin/sourcing/templates");
            setTemplateLibrary(data.rows || []);
        } catch { /* non-blocking */ }
    }, []);

    useEffect(() => { loadCredits(); loadTemplates(); }, [loadCredits, loadTemplates]);

    const run = async (nextPage = 1) => {
        setBusy(true);
        try {
            const payload = {
                titles: filters.titles.split(",").map((s) => s.trim()).filter(Boolean),
                location: filters.location || null,
                skills: filters.skills.split(",").map((s) => s.trim()).filter(Boolean),
                seniority: filters.seniority || null,
                page: nextPage,
                per_page: Number(filters.per_page) || 25,
            };
            const { data } = await api.post("/admin/sourcing/search", payload);
            setResults(data.results || []);
            setDuplicates(data.duplicates_skipped || 0);
            setPagination(data.pagination || null);
            setFilters((f) => ({ ...f, page: nextPage }));
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setBusy(false); }
    };

    const toggleSelect = (row) => {
        const key = row.apollo_person_id || row.email || row.linkedin_url;
        setSelected((s) => ({ ...s, [key]: s[key] ? undefined : row }));
    };

    const selectedRows = useMemo(() => Object.values(selected).filter(Boolean), [selected]);

    const revealEmail = async (row) => {
        const key = row.apollo_person_id || row.linkedin_url || row.email;
        setRevealing((r) => ({ ...r, [key]: true }));
        try {
            const { data } = await api.post("/admin/sourcing/reveal-email", {
                apollo_person_id: row.apollo_person_id,
                linkedin_url: row.linkedin_url,
                first_name: row.first_name,
                last_name: row.last_name,
                company: row.company,
            });
            if (!data.revealed || !data.email) {
                toast.info(data.note || "Apollo did not return an email.");
                return;
            }
            if (data.already_user) { toast.info("Already a Simonara user — will skip."); }
            if (data.already_contacted) { toast.info("Already in contact log — will skip."); }
            if (data.suppressed) { toast.info("On suppression list — will skip."); }
            // Patch the row in place.
            setResults((rs) => rs.map((r) => r === row ? { ...r, email: data.email, email_available: true } : r));
            loadCredits();
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setRevealing((r) => ({ ...r, [key]: undefined })); }
    };

    const bulkReveal = async () => {
        for (const row of selectedRows) {
            if (!row.email) await revealEmail(row);
        }
    };

    const openPreview = async () => {
        try {
            const { data } = await api.post("/admin/sourcing/preview", {
                first_name: selectedRows[0]?.first_name || "there",
                template_version: templateVersion || null,
            });
            setPreview(data);
            setPreviewOpen(true);
        } catch (err) { toast.error(formatApiError(err)); }
    };

    const sendInvites = async () => {
        const withEmail = selectedRows.filter((r) => r.email);
        if (!withEmail.length) {
            toast.error("Reveal at least one email first.");
            return;
        }
        setQueuing(true);
        try {
            const payload = {
                candidates: withEmail.map((r) => ({
                    apollo_person_id: r.apollo_person_id,
                    first_name: r.first_name,
                    last_name: r.last_name,
                    name: r.name,
                    email: r.email,
                    linkedin_url: r.linkedin_url,
                    title: r.title,
                    company: r.company,
                })),
                filters_used: filters,
                template_version: templateVersion || null,
            };
            const { data } = await api.post("/admin/sourcing/queue", payload);
            toast.success(`Queued ${data.added} invite(s). Skipped ${data.skipped}.`);
            setPreviewOpen(false);
            setSelected({});
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setQueuing(false); }
    };

    return (
        <div className="space-y-4" data-testid="sourcing-search">
            <div className="card-soft p-5 space-y-3">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                        <Label>Job titles / role keywords (comma-separated)</Label>
                        <Input value={filters.titles} onChange={(e) => setFilters({ ...filters, titles: e.target.value })}
                            placeholder="e.g. Barista, Coffee shop manager"
                            data-testid="sourcing-filter-titles" />
                    </div>
                    <div>
                        <Label>Location</Label>
                        <Input value={filters.location} onChange={(e) => setFilters({ ...filters, location: e.target.value })}
                            placeholder="e.g. Los Angeles, CA"
                            data-testid="sourcing-filter-location" />
                    </div>
                    <div>
                        <Label>Skills keywords (comma-separated)</Label>
                        <Input value={filters.skills} onChange={(e) => setFilters({ ...filters, skills: e.target.value })}
                            placeholder="e.g. espresso, latte art"
                            data-testid="sourcing-filter-skills" />
                    </div>
                    <div>
                        <Label>Seniority (optional)</Label>
                        <Input value={filters.seniority} onChange={(e) => setFilters({ ...filters, seniority: e.target.value })}
                            placeholder="e.g. senior, entry"
                            data-testid="sourcing-filter-seniority" />
                    </div>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                    <button onClick={() => run(1)} disabled={busy}
                        className="btn-green text-sm inline-flex items-center gap-2 disabled:opacity-60"
                        data-testid="sourcing-search-run">
                        {busy ? <><Loader2 size={14} className="animate-spin" /> Searching…</> : <><Search size={14} /> Search</>}
                    </button>
                    <span className="text-xs text-muted-foreground">
                        Search costs 0 Apollo credits. Only email reveals cost credits (1 each).
                    </span>
                </div>
            </div>

            {credits && (
                <div className="text-xs text-muted-foreground" data-testid="sourcing-credits">
                    Apollo status: {credits.available ? "connected" : "unavailable"} · {credits.note}
                </div>
            )}

            {results.length > 0 && (
                <>
                    <div className="flex items-center justify-between gap-2 flex-wrap" data-testid="sourcing-results-toolbar">
                        <div className="text-xs text-muted-foreground">
                            {results.length} result(s) · <span data-testid="sourcing-dupe-note">{duplicates} duplicate(s) skipped</span>
                            {pagination?.total_entries != null && ` · ${pagination.total_entries} total`}
                        </div>
                        <div className="flex items-center gap-2 flex-wrap">
                            <div className="text-xs">{selectedRows.length} selected</div>
                            <select
                                value={templateVersion}
                                onChange={(e) => setTemplateVersion(e.target.value)}
                                data-testid="sourcing-search-template-picker"
                                className="text-xs border border-border rounded-md bg-background px-2 py-1"
                                title="Choose a template for this campaign — leaves the active default unchanged"
                            >
                                <option value="">Use active template</option>
                                {templateLibrary.map((t) => (
                                    <option key={t.version} value={t.version}>
                                        {(t.name || t.version)}{t.active ? " (active)" : ""}
                                    </option>
                                ))}
                            </select>
                            <button onClick={bulkReveal} disabled={selectedRows.length === 0}
                                className="btn-outline text-xs inline-flex items-center gap-1 disabled:opacity-60"
                                data-testid="sourcing-bulk-reveal">
                                <Eye size={12} /> Reveal emails
                            </button>
                            <button onClick={openPreview} disabled={selectedRows.filter((r) => r.email).length === 0}
                                className="btn-green text-xs inline-flex items-center gap-1 disabled:opacity-60"
                                data-testid="sourcing-open-preview">
                                <UserPlus size={12} /> Add to invite queue
                            </button>
                        </div>
                    </div>

                    <div className="overflow-x-auto card-soft">
                        <table className="w-full text-sm">
                            <thead className="text-left text-xs text-muted-foreground border-b border-border">
                                <tr>
                                    <th className="p-2 w-8"></th>
                                    <th className="p-2">Name</th>
                                    <th className="p-2">Title</th>
                                    <th className="p-2">Company</th>
                                    <th className="p-2">Location</th>
                                    <th className="p-2">LinkedIn</th>
                                    <th className="p-2">Email</th>
                                </tr>
                            </thead>
                            <tbody data-testid="sourcing-results-tbody">
                                {results.map((r) => {
                                    const key = r.apollo_person_id || r.email || r.linkedin_url;
                                    const isSelected = !!selected[key];
                                    return (
                                        <tr key={key} className="border-b border-border last:border-0 hover:bg-muted/30">
                                            <td className="p-2">
                                                <input type="checkbox" checked={isSelected}
                                                    onChange={() => toggleSelect(r)}
                                                    data-testid={`sourcing-row-select-${key}`} />
                                            </td>
                                            <td className="p-2 font-medium">{r.name || "—"}</td>
                                            <td className="p-2 text-muted-foreground">{r.title || "—"}</td>
                                            <td className="p-2 text-muted-foreground">{r.company || "—"}</td>
                                            <td className="p-2 text-muted-foreground">{r.location || "—"}</td>
                                            <td className="p-2">
                                                {r.linkedin_url ? (
                                                    <a href={r.linkedin_url} target="_blank" rel="noreferrer"
                                                        className="text-emerald-600 hover:underline inline-flex items-center gap-1">
                                                        Link <ExternalLink size={11} />
                                                    </a>
                                                ) : "—"}
                                            </td>
                                            <td className="p-2">
                                                {r.email ? (
                                                    <span className="font-mono text-xs">{r.email}</span>
                                                ) : (
                                                    <button
                                                        onClick={() => revealEmail(r)}
                                                        disabled={!!revealing[key]}
                                                        className="text-xs inline-flex items-center gap-1 text-emerald-700 hover:underline disabled:opacity-60"
                                                        data-testid={`sourcing-reveal-${key}`}
                                                    >
                                                        {revealing[key] ? <Loader2 size={11} className="animate-spin" /> : <Eye size={11} />}
                                                        Reveal (1 credit)
                                                    </button>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>

                    {pagination?.total_pages > 1 && (
                        <div className="flex items-center justify-center gap-2">
                            <button disabled={filters.page <= 1 || busy}
                                onClick={() => run(filters.page - 1)}
                                className="btn-outline text-xs disabled:opacity-40">Prev</button>
                            <span className="text-xs text-muted-foreground">Page {filters.page} / {pagination.total_pages}</span>
                            <button disabled={filters.page >= pagination.total_pages || busy}
                                onClick={() => run(filters.page + 1)}
                                className="btn-outline text-xs disabled:opacity-40">Next</button>
                        </div>
                    )}
                </>
            )}

            {previewOpen && preview && (
                <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
                    onClick={() => setPreviewOpen(false)} data-testid="sourcing-preview-modal">
                    <div className="bg-background rounded-2xl max-w-2xl w-full p-6 max-h-[85vh] overflow-y-auto"
                        onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="font-heading font-semibold text-lg text-foreground">Preview & send</h3>
                            <button onClick={() => setPreviewOpen(false)}><X size={16} /></button>
                        </div>
                        <div className="mb-3 text-sm">
                            <div className="text-xs uppercase text-muted-foreground tracking-wider">Recipients</div>
                            <div className="mt-1">{selectedRows.filter((r) => r.email).length} candidate(s) with email · template <Badge className="border border-border ml-1">{preview.template_version}</Badge></div>
                        </div>
                        <div className="mb-3 text-sm">
                            <div className="text-xs uppercase text-muted-foreground tracking-wider">Subject</div>
                            <div className="mt-1 font-medium">{preview.subject}</div>
                        </div>
                        <div className="mb-4 text-sm">
                            <div className="text-xs uppercase text-muted-foreground tracking-wider">Body</div>
                            <div className="mt-2 border border-border rounded-lg p-3 bg-muted/30 max-h-[40vh] overflow-y-auto"
                                dangerouslySetInnerHTML={{ __html: preview.html }} />
                        </div>
                        <div className="flex items-center justify-end gap-2">
                            <button onClick={() => setPreviewOpen(false)} className="btn-outline text-sm">Cancel</button>
                            <button onClick={sendInvites} disabled={queuing}
                                className="btn-green text-sm inline-flex items-center gap-2 disabled:opacity-60"
                                data-testid="sourcing-confirm-send">
                                {queuing ? <><Loader2 size={13} className="animate-spin" /> Queueing…</> : <><Send size={13} /> Send invites</>}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

// ============================================================
// Invite queue tab
// ============================================================
const QueueTab = () => {
    const [rows, setRows] = useState([]);
    const [meta, setMeta] = useState({});
    const [busy, setBusy] = useState(false);

    const load = useCallback(async () => {
        setBusy(true);
        try {
            const { data } = await api.get("/admin/sourcing/queue");
            setRows(data.rows || []);
            setMeta({ sent_today: data.sent_today, daily_cap: data.daily_cap });
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setBusy(false); }
    }, []);

    useEffect(() => { load(); const t = setInterval(load, 15_000); return () => clearInterval(t); }, [load]);

    return (
        <div className="space-y-3" data-testid="sourcing-queue">
            <div className="flex items-center justify-between gap-2 flex-wrap">
                <div className="text-sm">
                    Sent today: <span className="font-mono font-bold">{meta.sent_today ?? "—"}</span> / {meta.daily_cap ?? "—"}
                </div>
                <button onClick={load} disabled={busy} className="btn-outline text-xs inline-flex items-center gap-1 disabled:opacity-60">
                    <RefreshCw size={12} className={busy ? "animate-spin" : ""} /> Refresh
                </button>
            </div>
            <div className="overflow-x-auto card-soft">
                <table className="w-full text-sm">
                    <thead className="text-left text-xs text-muted-foreground border-b border-border">
                        <tr>
                            <th className="p-2">Recipient</th>
                            <th className="p-2">Subject</th>
                            <th className="p-2">Scheduled</th>
                            <th className="p-2">Status</th>
                            <th className="p-2">Sent at</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((r) => (
                            <tr key={r.id} className="border-b border-border last:border-0" data-testid={`sourcing-queue-row-${r.id}`}>
                                <td className="p-2 font-mono text-xs">{r.recipient}</td>
                                <td className="p-2 text-muted-foreground">{r.subject}</td>
                                <td className="p-2 text-xs">{r.scheduled_at?.slice(0, 19).replace("T", " ")}</td>
                                <td className="p-2">
                                    <Badge className={
                                        r.status === "sent" ? "border border-emerald-500/40 bg-emerald-500/10 text-emerald-700" :
                                        r.status === "send_failed" ? "border border-rose-500/40 bg-rose-500/10 text-rose-700" :
                                        r.status === "canceled" ? "border border-muted-foreground/40 bg-muted text-muted-foreground" :
                                        "border border-amber-500/40 bg-amber-500/10 text-amber-700"
                                    }>{r.status}</Badge>
                                </td>
                                <td className="p-2 text-xs">{r.sent_at?.slice(0, 19).replace("T", " ") || "—"}</td>
                            </tr>
                        ))}
                        {rows.length === 0 && (
                            <tr><td colSpan={5} className="p-8 text-center text-muted-foreground text-sm">No invites in queue.</td></tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

// ============================================================
// Contact log tab
// ============================================================
const LogTab = () => {
    const [q, setQ] = useState("");
    const [status, setStatus] = useState("");
    const [rows, setRows] = useState([]);
    const [busy, setBusy] = useState(false);
    const [marking, setMarking] = useState({});

    const load = useCallback(async () => {
        setBusy(true);
        try {
            const { data } = await api.get("/admin/sourcing/log", { params: { q: q || undefined, status: status || undefined } });
            setRows(data.rows || []);
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setBusy(false); }
    }, [q, status]);

    useEffect(() => { load(); }, [load]);

    const markReplied = async (row) => {
        setMarking((m) => ({ ...m, [row.id]: true }));
        try {
            await api.post(`/admin/sourcing/log/${row.id}/mark-replied`, {});
            toast.success("Marked replied.");
            load();
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setMarking((m) => ({ ...m, [row.id]: undefined })); }
    };

    const exportCsv = async () => {
        try {
            const resp = await api.get("/admin/sourcing/log/export.csv", {
                params: { q: q || undefined, status: status || undefined },
                responseType: "blob",
            });
            const blob = new Blob([resp.data], { type: "text/csv;charset=utf-8" });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            // Best-effort filename; server also sets Content-Disposition.
            const ts = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 19);
            a.download = `sourcing-log-${ts}.csv`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            setTimeout(() => URL.revokeObjectURL(url), 1000);
        } catch (err) { toast.error(formatApiError(err)); }
    };

    return (
        <div className="space-y-3" data-testid="sourcing-log">
            <div className="flex items-center gap-2 flex-wrap">
                <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name / email / company"
                    className="max-w-sm" data-testid="sourcing-log-search" />
                <select value={status} onChange={(e) => setStatus(e.target.value)} className="text-sm border border-border rounded-md bg-background px-2 py-1"
                    data-testid="sourcing-log-status">
                    <option value="">All statuses</option>
                    <option value="queued">Queued</option>
                    <option value="sent">Sent</option>
                    <option value="bounced">Bounced</option>
                    <option value="opted-out">Opted-out</option>
                    <option value="replied">Replied</option>
                </select>
                <button onClick={load} disabled={busy} className="btn-outline text-xs">
                    {busy ? <Loader2 size={12} className="animate-spin" /> : "Search"}
                </button>
                <button onClick={exportCsv} className="btn-outline text-xs inline-flex items-center gap-1"
                    data-testid="sourcing-log-export-csv">
                    <ExternalLink size={12} /> Export CSV
                </button>
            </div>
            <div className="overflow-x-auto card-soft">
                <table className="w-full text-sm">
                    <thead className="text-left text-xs text-muted-foreground border-b border-border">
                        <tr>
                            <th className="p-2">Name</th>
                            <th className="p-2">Email</th>
                            <th className="p-2">Company</th>
                            <th className="p-2">Found</th>
                            <th className="p-2">Invited</th>
                            <th className="p-2">Template</th>
                            <th className="p-2">Status</th>
                            <th className="p-2">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((r) => (
                            <tr key={r.id} className="border-b border-border last:border-0" data-testid={`sourcing-log-row-${r.id}`}>
                                <td className="p-2 font-medium">{r.name || "—"}</td>
                                <td className="p-2 font-mono text-xs">{r.email}</td>
                                <td className="p-2 text-muted-foreground">{r.company || "—"}</td>
                                <td className="p-2 text-xs">{r.date_found?.slice(0, 10)}</td>
                                <td className="p-2 text-xs">{r.date_invited?.slice(0, 10) || "—"}</td>
                                <td className="p-2 text-xs">{r.template_version}</td>
                                <td className="p-2"><Badge className="border border-border">{r.status}</Badge></td>
                                <td className="p-2">
                                    {["sent", "opened", "queued"].includes(r.status) ? (
                                        <button
                                            onClick={() => markReplied(r)}
                                            disabled={!!marking[r.id]}
                                            data-testid={`sourcing-log-mark-replied-${r.id}`}
                                            className="text-[11px] inline-flex items-center gap-1 text-emerald-700 hover:underline disabled:opacity-60"
                                        >
                                            {marking[r.id] ? <Loader2 size={11} className="animate-spin" /> : <CheckCircle2 size={11} />}
                                            Mark replied
                                        </button>
                                    ) : <span className="text-muted-foreground text-xs">—</span>}
                                </td>
                            </tr>
                        ))}
                        {rows.length === 0 && (
                            <tr><td colSpan={8} className="p-8 text-center text-muted-foreground text-sm">No entries.</td></tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

// ============================================================
// Suppression tab
// ============================================================
const SuppressionTab = () => {
    const [rows, setRows] = useState([]);
    const [email, setEmail] = useState("");

    const load = useCallback(async () => {
        try {
            const { data } = await api.get("/admin/sourcing/suppression");
            setRows(data.rows || []);
        } catch (err) { toast.error(formatApiError(err)); }
    }, []);

    useEffect(() => { load(); }, [load]);

    const add = async () => {
        if (!email.trim()) return;
        try {
            await api.post("/admin/sourcing/suppress", { email: email.trim(), reason: "manual_admin" });
            toast.success("Added to suppression list.");
            setEmail(""); load();
        } catch (err) { toast.error(formatApiError(err)); }
    };

    return (
        <div className="space-y-3" data-testid="sourcing-suppression">
            <div className="flex items-center gap-2 flex-wrap">
                <Input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email to suppress"
                    className="max-w-sm" data-testid="sourcing-suppress-email" />
                <button onClick={add} className="btn-outline text-xs inline-flex items-center gap-1"
                    data-testid="sourcing-suppress-add">
                    <Ban size={12} /> Add
                </button>
            </div>
            <div className="overflow-x-auto card-soft">
                <table className="w-full text-sm">
                    <thead className="text-left text-xs text-muted-foreground border-b border-border">
                        <tr><th className="p-2">Email</th><th className="p-2">Reason</th><th className="p-2">Added</th></tr>
                    </thead>
                    <tbody>
                        {rows.map((r, i) => (
                            <tr key={i} className="border-b border-border last:border-0">
                                <td className="p-2 font-mono text-xs">{r.email}</td>
                                <td className="p-2 text-muted-foreground">{r.reason}</td>
                                <td className="p-2 text-xs">{r.created_at?.slice(0, 19).replace("T", " ")}</td>
                            </tr>
                        ))}
                        {rows.length === 0 && (
                            <tr><td colSpan={3} className="p-8 text-center text-muted-foreground text-sm">No suppressed addresses.</td></tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

// ============================================================
// Template + settings tab
// ============================================================
const SettingsTab = () => {
    const [templates, setTemplates] = useState([]);
    const [activeVersion, setActiveVersion] = useState(null);
    const [selectedVersion, setSelectedVersion] = useState(null);
    const [tpl, setTpl] = useState(null);          // the one being edited
    const [settings, setSettings] = useState(null);
    const [health, setHealth] = useState(null);
    const [busy, setBusy] = useState(false);
    const [editing, setEditing] = useState(false);
    const [creating, setCreating] = useState(false);

    const load = useCallback(async () => {
        try {
            const [{ data: lib }, { data: s }, { data: h }] = await Promise.all([
                api.get("/admin/sourcing/templates"),
                api.get("/admin/sourcing/settings"),
                api.get("/admin/sourcing/health"),
            ]);
            const rows = lib.rows || [];
            setTemplates(rows);
            const active = rows.find((r) => r.active) || rows[0];
            setActiveVersion(active?.version || null);
            // Preserve current selection if it still exists; otherwise pick active.
            setSelectedVersion((prev) => (rows.some((r) => r.version === prev) ? prev : (active?.version || null)));
            setTpl(rows.find((r) => r.version === (rows.some((r) => r.version === selectedVersion) ? selectedVersion : active?.version)) || null);
            setSettings(s); setHealth(h);
        } catch (err) { toast.error(formatApiError(err)); }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => { load(); }, [load]);

    // When the admin picks a different template from the sidebar, load it.
    useEffect(() => {
        if (!selectedVersion) return;
        const row = templates.find((r) => r.version === selectedVersion);
        if (row) { setTpl(row); setEditing(false); setCreating(false); }
    }, [selectedVersion, templates]);

    const saveTpl = async () => {
        if (!tpl?.version?.trim() || !tpl?.subject?.trim() || !tpl?.body?.trim()) {
            toast.error("Version, subject, and body are all required.");
            return;
        }
        setBusy(true);
        try {
            await api.put("/admin/sourcing/template", {
                version: tpl.version.trim(),
                name: tpl.name || tpl.version.trim(),
                subject: tpl.subject,
                body: tpl.body,
                active: false, // ignored server-side — saving no longer activates
            });
            toast.success("Template saved.");
            setEditing(false); setCreating(false);
            setSelectedVersion(tpl.version.trim());
            load();
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setBusy(false); }
    };

    const activate = async (version) => {
        setBusy(true);
        try {
            await api.post(`/admin/sourcing/template/${encodeURIComponent(version)}/activate`, {});
            toast.success("Template activated.");
            load();
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setBusy(false); }
    };

    const del = async (version) => {
        if (!window.confirm(`Delete template "${version}"? This cannot be undone.`)) return;
        setBusy(true);
        try {
            await api.delete(`/admin/sourcing/template/${encodeURIComponent(version)}`);
            toast.success("Template deleted.");
            setSelectedVersion(activeVersion);
            load();
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setBusy(false); }
    };

    const startCreate = () => {
        const stamp = new Date().toISOString().slice(0, 10).replace(/-/g, "");
        setCreating(true);
        setEditing(true);
        setTpl({
            version: `campaign-${stamp}`,
            name: `Campaign ${stamp}`,
            subject: "",
            body: "Hi {first_name} — {signup_link}\n\n— Shirley",
            active: false,
        });
    };

    const saveSettings = async () => {
        setBusy(true);
        try {
            await api.put("/admin/sourcing/settings", settings);
            toast.success("Settings saved.");
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setBusy(false); }
    };

    if (!tpl || !settings || !health) {
        return <div className="py-12 flex justify-center"><Loader2 size={16} className="animate-spin text-emerald-500" /></div>;
    }

    return (
        <div className="space-y-6" data-testid="sourcing-settings">
            {!health.apollo_configured && (
                <div className="rounded-xl border border-rose-500/30 bg-rose-500/10 p-3 text-sm inline-flex items-start gap-2">
                    <AlertTriangle size={16} className="text-rose-500 mt-0.5" />
                    <div>Apollo API key missing. Set <code>APOLLO_API_KEY</code> in the backend environment.</div>
                </div>
            )}
            {!health.physical_address_configured && (
                <div className="rounded-xl border border-amber-500/30 bg-amber-500/10 p-3 text-sm inline-flex items-start gap-2">
                    <AlertTriangle size={16} className="text-amber-500 mt-0.5" />
                    <div>
                        Physical mailing address placeholder is being used. Set <code>PHYSICAL_MAILING_ADDRESS</code> for CAN-SPAM compliance.<br/>
                        <span className="text-muted-foreground text-xs">Current: {health.physical_address_preview}</span>
                    </div>
                </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-[220px,1fr] gap-4" data-testid="sourcing-templates-library">
                {/* Library sidebar */}
                <aside className="card-soft p-3">
                    <div className="flex items-center justify-between mb-2">
                        <div className="overline">Templates</div>
                        <button onClick={startCreate} className="btn-outline text-[11px] inline-flex items-center gap-1"
                            data-testid="sourcing-template-new">
                            + New
                        </button>
                    </div>
                    <ul className="space-y-1">
                        {templates.map((t) => {
                            const isSel = t.version === selectedVersion || (creating && !templates.some((x) => x.version === selectedVersion));
                            const isActive = t.version === activeVersion;
                            return (
                                <li key={t.version}>
                                    <button
                                        onClick={() => { setSelectedVersion(t.version); setCreating(false); }}
                                        data-testid={`sourcing-template-item-${t.version}`}
                                        className={`w-full text-left text-xs rounded-md px-2 py-1.5 transition ${isSel ? "bg-emerald-500/10 text-emerald-700 dark:text-emerald-400" : "text-foreground hover:bg-muted/40"}`}
                                    >
                                        <div className="font-semibold truncate">{t.name || t.version}</div>
                                        <div className="mt-0.5 flex items-center gap-1 flex-wrap">
                                            {isActive && <span className="text-[9px] px-1.5 py-0.5 rounded-full border border-emerald-500/40 bg-emerald-500/10 text-emerald-700 uppercase font-bold tracking-wider">Active</span>}
                                            <span className="text-[10px] text-muted-foreground truncate">{t.version}</span>
                                        </div>
                                    </button>
                                </li>
                            );
                        })}
                        {creating && !templates.some((x) => x.version === (tpl?.version || "")) && (
                            <li className="text-[11px] text-emerald-700 dark:text-emerald-400 px-2">
                                New draft (unsaved)
                            </li>
                        )}
                    </ul>
                </aside>

                {/* Editor */}
                <div className="card-soft p-5 space-y-3" data-testid="sourcing-template-card">
                    <div className="flex items-center justify-between gap-2 flex-wrap">
                        <div className="flex items-center gap-2 flex-wrap">
                            <h3 className="font-heading font-semibold text-foreground">{creating ? "New template" : (tpl.name || tpl.version)}</h3>
                            {tpl.version === activeVersion && !creating && (
                                <span className="text-[10px] px-2 py-0.5 rounded-full border border-emerald-500/40 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 uppercase font-bold tracking-wider">Active</span>
                            )}
                        </div>
                        <div className="flex items-center gap-2 flex-wrap">
                            {!editing ? (
                                <button onClick={() => setEditing(true)} className="btn-outline text-xs inline-flex items-center gap-1"
                                    data-testid="sourcing-template-edit">
                                    <Pencil size={12} /> Edit
                                </button>
                            ) : (
                                <button onClick={saveTpl} disabled={busy} className="btn-green text-xs inline-flex items-center gap-1 disabled:opacity-60"
                                    data-testid="sourcing-template-save">
                                    {busy ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />} Save
                                </button>
                            )}
                            {!creating && tpl.version !== activeVersion && !tpl.is_default && (
                                <button onClick={() => activate(tpl.version)} disabled={busy} className="btn-outline text-xs inline-flex items-center gap-1 disabled:opacity-60"
                                    data-testid="sourcing-template-activate">
                                    <CheckCircle2 size={12} /> Make active
                                </button>
                            )}
                            {!creating && tpl.version !== activeVersion && !tpl.is_default && (
                                <button onClick={() => del(tpl.version)} disabled={busy} className="btn-outline text-xs inline-flex items-center gap-1 disabled:opacity-60 text-rose-700 border-rose-500/40"
                                    data-testid="sourcing-template-delete">
                                    <X size={12} /> Delete
                                </button>
                            )}
                        </div>
                    </div>
                    <div>
                        <Label>Version</Label>
                        <Input value={tpl.version} disabled={!creating} onChange={(e) => setTpl({ ...tpl, version: e.target.value })}
                            data-testid="sourcing-template-version" />
                    </div>
                    <div>
                        <Label>Name (label for the library)</Label>
                        <Input value={tpl.name || ""} disabled={!editing} onChange={(e) => setTpl({ ...tpl, name: e.target.value })}
                            data-testid="sourcing-template-name" placeholder="e.g. Hospitality outreach" />
                    </div>
                    <div>
                        <Label>Subject</Label>
                        <Input value={tpl.subject} disabled={!editing} onChange={(e) => setTpl({ ...tpl, subject: e.target.value })}
                            data-testid="sourcing-template-subject" />
                    </div>
                    <div>
                        <Label>Body — use <code>{"{first_name}"}</code> and <code>{"{signup_link}"}</code></Label>
                        <Textarea value={tpl.body} rows={10} disabled={!editing} onChange={(e) => setTpl({ ...tpl, body: e.target.value })}
                            data-testid="sourcing-template-body" />
                    </div>
                    <div className="text-[11px] text-muted-foreground italic">
                        Simonara LLC · CAN-SPAM footer (physical address + one-click unsubscribe) is appended automatically to every send. Editing a template never activates it — click <b>Make active</b> to swap.
                    </div>
                </div>
            </div>

            <div className="card-soft p-5 space-y-3">
                <h3 className="font-heading font-semibold text-foreground">Send pacing</h3>
                <div className="grid grid-cols-3 gap-3">
                    <div>
                        <Label>Daily cap</Label>
                        <Input type="number" value={settings.daily_cap} onChange={(e) => setSettings({ ...settings, daily_cap: Number(e.target.value) })}
                            data-testid="sourcing-settings-cap" />
                    </div>
                    <div>
                        <Label>Delay min (s)</Label>
                        <Input type="number" value={settings.delay_min} onChange={(e) => setSettings({ ...settings, delay_min: Number(e.target.value) })}
                            data-testid="sourcing-settings-delay-min" />
                    </div>
                    <div>
                        <Label>Delay max (s)</Label>
                        <Input type="number" value={settings.delay_max} onChange={(e) => setSettings({ ...settings, delay_max: Number(e.target.value) })}
                            data-testid="sourcing-settings-delay-max" />
                    </div>
                </div>
                <button onClick={saveSettings} disabled={busy} className="btn-green text-xs inline-flex items-center gap-1 disabled:opacity-60"
                    data-testid="sourcing-settings-save">
                    {busy ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />} Save settings
                </button>
            </div>
        </div>
    );
};

// ============================================================
// Page shell
// ============================================================
const AdminSourcing = () => {
    const [tab, setTab] = useState("search");

    return (
        <div data-testid="admin-sourcing-page">
            <div className="mb-6 flex items-center justify-between gap-2 flex-wrap">
                <div>
                    <p className="overline">Talent acquisition</p>
                    <h1 className="mt-1 font-heading font-medium text-2xl sm:text-3xl tracking-tight text-foreground">
                        Sourcing
                    </h1>
                    <p className="mt-1 text-sm text-muted-foreground max-w-2xl">
                        Search Apollo for professionals, reveal emails, and invite them to
                        create a free Talent Passport. Admin-only — never visible to employers
                        or talent.
                    </p>
                </div>
                <div className="text-xs text-emerald-700 dark:text-emerald-400 inline-flex items-center gap-1">
                    <Shield size={12} /> Admin-only · CAN-SPAM compliant
                </div>
            </div>

            <div className="mb-6 flex gap-1 flex-wrap" data-testid="sourcing-tabs">
                {TAB_KEYS.map((k) => (
                    <button
                        key={k}
                        onClick={() => setTab(k)}
                        data-testid={`sourcing-tab-${k}`}
                        className={`text-sm px-3 py-1.5 rounded-lg border transition ${tab === k
                                ? "border-emerald-500 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400"
                                : "border-border text-muted-foreground hover:bg-muted/40"}`}
                    >
                        {TAB_LABELS[k]}
                    </button>
                ))}
            </div>

            {tab === "search" && <SearchTab />}
            {tab === "queue" && <QueueTab />}
            {tab === "log" && <LogTab />}
            {tab === "suppression" && <SuppressionTab />}
            {tab === "settings" && <SettingsTab />}
        </div>
    );
};

export default AdminSourcing;
